import React from 'react';
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router-dom';
import { connect } from 'react-redux';
import _ from 'lodash';
import { withTranslation, WithTranslation } from 'react-i18next';

import * as api from '../../../../api';
import * as store from '../../../../reducers/store';
import * as logger from '../../../../logger';
import { isFormTouched } from '../../../../utils/form';
import { parseValue } from 'common/dist/utils/types';
import { makeFetchInsuranceCards } from '../../../../reducers/patient';
import { sendToOcr } from '../../UploadDocumentPage/utils';
import { getInsuranceErrors, maybeUpdateInsuranceDetails } from './utils';

import CardListPage from '../CardListPage';
import FormDetailsPage from '../FormDetailsPage';
import Loader from '../../../../components/Loader';
import ReviewPage from '../ReviewPage';
import UploadPage from '../UploadPage';

import { Button, Size } from '../../../../components/Button';
import { FormOfPayment } from '../FormOfPayment';
import { InfoMessage } from '../../../../components/InfoMessage';
import { sendEventMessage, Message } from '../../../../utils/native';
import {
  getSelectedForms,
  getSelectedPracticePreference,
} from '../../../../utils/formSet';

import defaultStyle from '../../../../styles/formSetStyles.module.css';
import uploadStyles from '../../UploadDocumentPage/index.module.css';
import { Side } from '../../../../utils/enum';

interface Props
  extends RouteComponentProps,
    StateProps,
    DispatchProps,
    WithTranslation {
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  navigateToNextPage: () => Promise<void>;
  setFormField: (formFieldId: string, value: string) => void;
  encounter: api.Encounter.Encounter;
}

interface State {
  errorMessage: string;
  ocrResults: api.InsuranceCardOcrResult.InsuranceCardOcrResult | undefined;
  mounted: boolean;
}

interface StateProps {
  cards: store.Loaded['insuranceCards']['data'];
  cardsLoading: store.Loaded['insuranceCards']['loading'];
  collectSubscriberInfo: store.Loaded['masterPracticePreference']['collectSubscriberInfo'];
  enablePrevisitSubscriberToGuarantorRelationshipCollection: store.Loaded['masterPracticePreference']['enablePrevisitSubscriberToGuarantorRelationshipCollection'];
  forms: store.Forms;
  selectedFormSetId: store.FormSetSelected['selectedFormSetId'];
}

interface DispatchProps {
  fetchInsuranceCards: (previsitFormSetId: string) => Promise<void>;
}

interface UpdateInsuranceFormValue
  extends Omit<
    Partial<api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue>,
    'footprint'
  > {
  footprint?: api.PrevisitPatientInsurance.Footprint;
}

export function getEmptyValue(): api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue {
  return {
    subscribers: [],
    addedInsuranceIds: [],
    confirmedInsuranceIds: [],
    invalidInsuranceIds: [],
  };
}

export function parseFieldValue(
  value: string,
): api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue {
  let fieldValue:
    | api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue
    | undefined;
  try {
    fieldValue = JSON.parse(value);
  } catch (err) {
    logger.error(err, 'failed to parse field value for insurance card');
  }
  return fieldValue || getEmptyValue();
}

class MultiInsurancePageSelector extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      errorMessage: '',
      ocrResults: undefined,
      mounted: false,
    };
  }

  async componentDidMount() {
    await this.props.fetchInsuranceCards(this.props.selectedFormSetId);
    this.setState({ mounted: true });
  }

  updateInsuranceFormValue = async (newValues: UpdateInsuranceFormValue) => {
    const { field, setFormField } = this.props;
    const currentValue = parseFieldValue(field.value);

    const stringValue = JSON.stringify({ ...currentValue, ...newValues });
    setFormField(field.id, stringValue);
    try {
      await api.client.Previsit.updateFormField(
        field.previsitFormSetId,
        field.id,
        stringValue,
      );
    } catch (err) {
      logger.error(err, 'ran into error trying to update field value');
    }
    sendEventMessage(Message.ActiveAction);
  };

  handlePaymentChoice = async (payingWithInsurance: boolean) => {
    const { navigateToNextPage, history, location } = this.props;
    if (payingWithInsurance) {
      await this.updateInsuranceFormValue({
        payingWithInsurance: true,
        footprint:
          api.PrevisitPatientInsurance.Footprint.AnsweredPayingWithInsurance,
      });
      history.push(location.pathname + '/add' + this.props.location.search);
      return;
    }
    await this.updateInsuranceFormValue({
      payingWithInsurance,
      footprint:
        api.PrevisitPatientInsurance.Footprint.AnsweredPayingWithoutInsurance,
    });
    await navigateToNextPage();
  };

  getCardById = (cardId?: string): store.InsuranceCard | undefined => {
    return this.props.cards.find((card) => card.id === cardId);
  };

  handleRemoveInsurance = async (cardId: string) => {
    const card = this.getCardById(cardId);
    const uploadId = card?.patientUploadId;
    try {
      uploadId &&
        (await api.client.PatientUpload.invalidateCurrentDocument(uploadId));
      card &&
        (await api.client.PrevisitPatientInsuranceInformation.remove({
          insuranceInformationId: card.id,
          encounterId: this.props.encounter.id,
        }));
    } catch (err) {
      logger.error(err, `failed to invalidate document with id of ${uploadId}`);
    }

    if (card) {
      const { field } = this.props;
      const currentValue = parseFieldValue(field.value);
      await this.maybeRemoveFormFieldInsurance(card.id);
      await this.updateInsuranceFormValue({
        addedInsuranceIds:
          currentValue.addedInsuranceIds?.filter((id) => id !== cardId) || [],
        confirmedInsuranceIds:
          currentValue.confirmedInsuranceIds?.filter((id) => id !== cardId) ||
          [],
        ...(!currentValue.addedInsuranceIds?.includes(cardId) && {
          invalidInsuranceIds: [
            ...(currentValue.invalidInsuranceIds
              ? currentValue.invalidInsuranceIds
              : []),
            cardId,
          ], // invalidate iff insurance was ingested (e.g. not in added)
        }),
      });
    }
    await this.props.fetchInsuranceCards(this.props.selectedFormSetId);
  };

  shouldAskSubscriberInfo = () => {
    const {
      collectSubscriberInfo,
      field: { primitive },
    } = this.props;

    if (
      primitive ===
      api.PrevisitFormTemplateFieldConfig.Primitive
        .PatientInsuranceCardPhotoUpload
    ) {
      return false;
    }

    return collectSubscriberInfo;
  };

  handleReviewContinue = (reviewingId: string) => {
    const { cards } = this.props;
    const cardIndex = cards.findIndex((c) => c.id === reviewingId);
    if (cardIndex === cards.length - 1) {
      // last entry, so navigate to list
      this.props.history.push('../list' + this.props.location.search);
      return;
    }

    const nextCardId = cards[cardIndex + 1].id;
    this.props.history.push(
      `../${nextCardId}/review` + this.props.location.search,
    );
  };

  handleEditContinue = async (cardId: string) => {
    const { field } = this.props;
    const currentValue = parseFieldValue(field.value);
    await this.updateInsuranceFormValue({
      payingWithInsurance: true,
      footprint: api.PrevisitPatientInsurance.Footprint.AnsweredEditContinue,
      ...(!currentValue.addedInsuranceIds?.includes(cardId) &&
        !currentValue.confirmedInsuranceIds?.includes(cardId) && {
          confirmedInsuranceIds: [
            ...(currentValue.confirmedInsuranceIds
              ? currentValue.confirmedInsuranceIds
              : []),
            cardId,
          ],
        }),
    });

    if (this.shouldAskSubscriberInfo()) {
      // NEW: allow users to modify their subscriber information on edit, but not review, if allowed
      this.props.history.push('form' + this.props.location.search);
      return;
    }

    this.props.history.push('../list' + this.props.location.search);
  };

  navigateToEdit = (cardIndex: number) => {
    const cardId = this.props.cards[cardIndex].id;
    this.props.history.push(`${cardId}/edit` + this.props.location.search);
  };

  navigateToUpload = () => {
    this.props.history.push('add' + this.props.location.search);
  };

  navigateToReplace = () => {
    this.props.history.push('replace' + this.props.location.search);
  };

  handleAddUpload = async (documentId: string) => {
    const cardId = this.props.cards.find(
      (c) => c.patientUploadId === documentId,
    )?.id;

    if (!cardId) {
      // should never reach here, but fallback just in case
      this.props.history.push(`../list` + this.props.location.search);
      return;
    }

    // add new card to tracking
    const { field } = this.props;
    const currentValue = parseFieldValue(field.value);
    await this.updateInsuranceFormValue({
      addedInsuranceIds: [
        ...(currentValue.addedInsuranceIds
          ? currentValue.addedInsuranceIds
          : []),
        cardId,
      ],
    });

    // replace url is "/:insuranceId/replace" while add url is "/add"
    const isReplacing = this.props.location.pathname.includes('replace');
    const urlPrefix = isReplacing ? '../' : '';
    if (this.shouldAskSubscriberInfo()) {
      // specifically replace to avoid back into replacement
      return this.props.history.replace(
        `${urlPrefix}${cardId}/form` + this.props.location.search,
      );
    }

    return this.props.history.push(
      `${urlPrefix}list` + this.props.location.search,
    );
  };

  handleAddCancel = () => {
    /**
     * When going `card list screen` -> `add` -> `cancel` it should go back to the list screen by replace /add to /list
     * When going `card list screen` -> `edit existing` -> `cancel` it should replace `/:id/replace` to `/list`, so here we need to push `../list`
     */
    const isReplacing = this.props.location.pathname.includes('replace');
    const urlPrefix = isReplacing ? '../' : '';
    return this.props.history.push(
      `${urlPrefix}list` + this.props.location.search,
    );
  };

  handleAddSkip = async () => {
    await this.updateInsuranceFormValue({
      footprint: api.PrevisitPatientInsurance.Footprint.SkippedInsurance,
    });
    await this.props.navigateToNextPage();
  };

  handleOcr = async (uploadId: string, side: Side, payor?: string) => {
    const ocrResults = await sendToOcr(uploadId, side, payor);
    if (ocrResults && side === Side.Front) {
      this.setState({ ocrResults });
    }

    return ocrResults;
  };

  handleInsuranceStillValid = async (card: store.InsuranceCard) => {
    const { id, name } = card;
    const { field } = this.props;
    const isReviewingGeneratedCard = !!id && name !== 'placeholder';
    const value = parseFieldValue(field.value);
    const alreadyExist =
      isReviewingGeneratedCard &&
      value.insuranceCoverages?.some((insurance) => insurance.id === card.id);

    await this.updateInsuranceFormValue({
      payingWithInsurance: true,
      footprint:
        api.PrevisitPatientInsurance.Footprint.AnsweredReviewStillValid,

      confirmedInsuranceIds: [
        ...(value.confirmedInsuranceIds ? value.confirmedInsuranceIds : []),
        card.id,
      ],
      ...(!alreadyExist && {
        insuranceCoverages: [
          ...(value.insuranceCoverages || []),
          {
            id: card.id,
            payor: card.payor,
            primary: false,
            primaryPolicyHolderMemberId:
              card.primaryPolicyHolderMemberId || card.memberId,
            groupNumber: card.groupNumber,
            coverageStartDate: card.coverageStartDate,
            subscriberId: card.id,
          },
        ],
      }),
    });
  };

  maybeRemoveFormFieldInsurance = async (cardId: string) => {
    const { field } = this.props;
    const value = parseFieldValue(field.value);
    await this.updateInsuranceFormValue({
      footprint: api.PrevisitPatientInsurance.Footprint.RemovedInsurance,
      insuranceCoverages: (value.insuranceCoverages || []).filter(
        (insurance) => insurance.id !== cardId,
      ),
    });
  };

  submitFormDetails = async (
    value: api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue,
  ) => {
    this.updateInsuranceFormValue({
      ...value,
      footprint:
        api.PrevisitPatientInsurance.Footprint.SubmittedSubscriberDetails,
    });
    await maybeUpdateInsuranceDetails(
      value,
      this.state.ocrResults,
      this.props.encounter.id,
    );
    await this.props.fetchInsuranceCards(this.props.selectedFormSetId);
    this.setState({ ocrResults: undefined });
    this.props.history.push('../list' + this.props.location.search);
  };

  renderAddPage = (
    props: RouteComponentProps<{ insuranceInformationId?: string }>,
  ) => {
    const { cards, fetchInsuranceCards, field, t } = this.props;
    const { insuranceInformationId } = props.match.params;
    const card = this.getCardById(insuranceInformationId);

    const isCardPrimary =
      !cards.length || (!!card && card.primary && !card.isDeleted);
    const addType = isCardPrimary ? 'primary' : 'additional';
    const deletePrimaryOnComplete =
      addType === 'primary' && !!card?.patientUploadId;

    const isChangingPictures = card && props.match.path.includes('replace');

    // not skipping the question if cards exist
    const isSkipping = !cards.length;
    const label = t(
      `Upload photos of your ${addType} health insurance card. Additional cards may be uploaded after.`,
    );
    return (
      <UploadPage
        required={field.required && addType === 'primary'}
        documentType={api.PatientUpload.DocumentType.InsuranceCard}
        label={label}
        skipLabel={
          isSkipping
            ? t(`I don't have my card`)
            : t(`Go back to insurance list`)
        }
        type="Insurance Card"
        uploadParams={{ primary: isCardPrimary }}
        onSkip={isSkipping ? this.handleAddSkip : this.handleAddCancel}
        onUploadComplete={async (documentId) => {
          if (deletePrimaryOnComplete || isChangingPictures) {
            await this.handleRemoveInsurance(insuranceInformationId!);
          }
          await fetchInsuranceCards(this.props.selectedFormSetId);
          await this.handleAddUpload(documentId);
        }}
        showEditTool={true}
        sendToOcr={this.handleOcr}
        updateInsuranceFootprintValue={async (
          footprint: api.PrevisitPatientInsurance.Footprint,
        ) => {
          await this.updateInsuranceFormValue({ footprint });
        }}
        encounter={this.props.encounter}
      />
    );
  };

  renderEditPage = (
    routerProps: RouteComponentProps<{ insuranceInformationId: string }>,
  ) => {
    const { t, cardsLoading } = this.props;
    const { insuranceInformationId } = routerProps.match.params;
    const card = this.getCardById(insuranceInformationId);

    if (cardsLoading || !this.state.mounted) return <Loader />;
    if (!card) {
      return (
        <Redirect
          to={{ pathname: '../list', search: this.props.location.search }}
        />
      );
    }

    const reviewLabel = card.primary
      ? t('Is this still your primary insurance?')
      : t('Is this insurance still accurate and active?');
    return (
      <ReviewPage label={reviewLabel} card={card}>
        <div className={defaultStyle.ButtonSubmitWrapper}>
          <Button
            type="button"
            children={t('Continue')}
            onClick={() => this.handleEditContinue(card.id)}
          />
          <Button
            type="button"
            children={t('Change pictures')}
            onClick={this.navigateToReplace}
            size={Size.SmallHorizontalPadding}
          />
          {!card.primary && (
            <Button
              className={uploadStyles.DeleteButton}
              type="button"
              children={t('Remove this insurance')}
              onClick={async () => {
                await this.handleRemoveInsurance(insuranceInformationId);
                this.props.history.push('../list' + this.props.location.search);
              }}
            />
          )}
        </div>
      </ReviewPage>
    );
  };

  renderReviewPage = (
    routerProps: RouteComponentProps<{ insuranceInformationId: string }>,
  ) => {
    const { cardsLoading, t } = this.props;
    const { insuranceInformationId } = routerProps.match.params;
    const card = this.getCardById(insuranceInformationId);

    if (cardsLoading || !this.state.mounted) return <Loader />;
    if (!card) {
      return (
        <Redirect
          to={{ pathname: '../list', search: this.props.location.search }}
        />
      );
    }

    const editLabel = card.primary
      ? t('Is this still your primary insurance?')
      : t('Is this insurance still accurate and active?');
    return (
      <ReviewPage label={editLabel} card={card}>
        <div className={defaultStyle.ButtonSubmitWrapper}>
          <Button
            type="button"
            children={t('Yes')}
            onClick={() => {
              this.handleInsuranceStillValid(card);
              this.handleReviewContinue(card.id);
            }}
          />
          {card.primary && (
            <>
              <Button
                type="button"
                children={t('No, my insurance has changed')}
                onClick={async () => {
                  await this.updateInsuranceFormValue({
                    payingWithInsurance: true,
                    footprint:
                      api.PrevisitPatientInsurance.Footprint
                        .AnsweredInsuranceHasChanged,
                  });
                  this.navigateToReplace();
                }}
                size={Size.SmallHorizontalPadding}
              />
              <Button
                type="button"
                children={t(`No, I'm paying out of pocket`)}
                onClick={() => this.handlePaymentChoice(false)}
              />
            </>
          )}
          {!card.primary && (
            <Button
              className={uploadStyles.DeleteButton}
              type="button"
              children={t('No, remove this insurance')}
              onClick={async () => {
                // may want to consider save next index -> delete -> change page for less flash
                this.handleReviewContinue(card.id);
                await this.handleRemoveInsurance(insuranceInformationId);
              }}
            />
          )}
        </div>
      </ReviewPage>
    );
  };

  renderPolicyHolderForm = (
    routerProps: RouteComponentProps<{ insuranceInformationId: string }>,
  ) => {
    const { mounted, ocrResults } = this.state;
    const { cardsLoading, forms, field } = this.props;
    const { insuranceInformationId } = routerProps.match.params;
    const card = this.getCardById(insuranceInformationId);
    if (cardsLoading || !mounted) return <Loader />;
    if (!card) {
      return (
        <Redirect
          to={{ pathname: '../list', search: this.props.location.search }}
        />
      );
    }

    const guarantorFormField = _.flatten(
      Object.values(forms).map((form) => form.fields),
    ).find(
      (formField) =>
        formField.primitive ===
        api.PrevisitFormTemplateFieldConfig.Primitive.PatientGuarantors,
    );
    return (
      <FormDetailsPage
        onSubmit={this.submitFormDetails}
        insuranceInformation={card}
        ocrResults={ocrResults}
        value={parseFieldValue(field.value)}
        guarantorValues={
          (guarantorFormField?.value &&
            parseValue(guarantorFormField?.value)) ||
          undefined
        }
        helpDialog={field.helpDialog}
      />
    );
  };

  renderInsuranceList = () => {
    const {
      cards,
      field,
      forms,
      navigateToNextPage,
      history,
      location,
      enablePrevisitSubscriberToGuarantorRelationshipCollection,
    } = this.props;

    if (!cards.length) {
      // no cards to list, so redirect back to landing
      history.replace('.' + location.search);
      return null;
    }

    const guarantorFormField = _.flatten(
      Object.values(forms).map((form) => form.fields),
    ).find(
      (formField) =>
        formField.primitive ===
        api.PrevisitFormTemplateFieldConfig.Primitive.PatientGuarantors,
    );

    const selectedGuarantorFieldValue =
      guarantorFormField &&
      parseValue<api.Guarantor.PrevisitGuarantorFormFieldValue>(
        guarantorFormField.value,
      );
    const selectedGuarantor =
      selectedGuarantorFieldValue &&
      selectedGuarantorFieldValue.guarantors?.find(
        (g) => g.id === selectedGuarantorFieldValue.selectedGuarantorId,
      );

    const cardErrors = getInsuranceErrors(
      cards,
      parseFieldValue(field.value),
      {
        enablePrevisitSubscriberToGuarantorRelationshipCollection,
        collectSubscriberInfo: this.shouldAskSubscriberInfo(),
      },
      selectedGuarantor ?? undefined,
    );

    return (
      <CardListPage
        cardErrors={cardErrors}
        navigateToNextPage={async () => {
          const insuranceInformation = parseFieldValue(field.value);
          if (
            field.required &&
            typeof insuranceInformation.payingWithInsurance !== 'boolean'
          ) {
            this.setState({
              errorMessage:
                'Please review your existing insurance(s) by pressing on "Edit"',
            });
            window.scrollTo(0, 0);
            return;
          }
          if (!_.isEmpty(cardErrors)) {
            this.setState({
              errorMessage:
                'Please verify your insurance details by pressing on "Edit"',
            });
            window.scrollTo(0, 0);
            return;
          }
          await navigateToNextPage();
        }}
        onError={(errorMessage) => this.setState({ errorMessage })}
        navigateToUpload={() => {
          this.setState({ errorMessage: '' });
          this.navigateToUpload();
        }}
        navigateToEdit={(index) => {
          this.setState({ errorMessage: '' });
          this.navigateToEdit(index);
        }}
      />
    );
  };

  renderLanding = () => {
    const { cards, field, history, location } = this.props;
    if (cards.length && isFormTouched([field])) {
      history.replace(location.pathname + '/list' + location.search);
      return <Loader />;
    }

    if (cards.length) {
      history.replace(
        location.pathname + `/${cards[0].id}/review` + location.search,
      );
      return <Loader />;
    }

    return (
      <FormOfPayment
        onPaymentChoice={this.handlePaymentChoice}
        labelOverride={field.sublabel}
      />
    );
  };

  render() {
    const { errorMessage, mounted } = this.state;
    const { cardsLoading, location, match } = this.props;

    if (cardsLoading || !mounted) return <Loader />;

    return (
      <div>
        {errorMessage && (
          <div className={uploadStyles.InfoMessage}>
            <InfoMessage isError={true}>{errorMessage}</InfoMessage>
          </div>
        )}
        <Switch>
          <Redirect
            from="/:url*(/+)" // prevents trailing slash, which interferes with routing
            to={location.pathname.slice(0, -1) + location.search}
          />
          <Route exact path={`${match.path}/`} render={this.renderLanding} />
          <Route path={`${match.path}/add`} render={this.renderAddPage} />
          <Route
            path={`${match.path}/:insuranceInformationId/edit`}
            render={this.renderEditPage}
          />
          <Route
            path={`${match.path}/:insuranceInformationId/replace`}
            render={this.renderAddPage}
          />
          <Route
            path={`${match.path}/:insuranceInformationId/review`}
            render={this.renderReviewPage}
          />
          <Route
            path={`${match.path}/:insuranceInformationId/form`}
            render={this.renderPolicyHolderForm}
          />
          <Route
            path={`${match.path}/list`}
            render={this.renderInsuranceList}
          />
        </Switch>
      </div>
    );
  }
}

const mapStateToProps = (state: store.FormSetSelected): StateProps => {
  const selectedPracticePreference = getSelectedPracticePreference(state);
  return {
    cards: state.insuranceCards.data,
    cardsLoading: state.insuranceCards.loading,
    collectSubscriberInfo: selectedPracticePreference.collectSubscriberInfo,
    enablePrevisitSubscriberToGuarantorRelationshipCollection:
      selectedPracticePreference.enablePrevisitSubscriberToGuarantorRelationshipCollection,
    forms: getSelectedForms(state.formSets, state.selectedFormSetId),
    selectedFormSetId: state.selectedFormSetId,
  };
};

const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
  fetchInsuranceCards: makeFetchInsuranceCards(dispatch),
});

const ConnectedMultiInsurance = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(MultiInsurancePageSelector));

export default withRouter(ConnectedMultiInsurance);
